import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { RichTextEditor } from '../ui/rich-text-editor';
import { Label } from '../ui/label';
import { Tabs, TabsContent } from '../ui/tabs';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menubar,
  MenubarMenu,
  MenubarTrigger,
} from "@/components/ui/menubar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ConfirmDialog } from '../ui/confirm-dialog';
// Dialog removed: edit uses inline form identical to create
import { 
  FileText, 
  Plus, 
  Edit, 
  Trash2,
  Eye,
  Search,
  Calendar,
  User,
  Image as ImageIcon,
  Upload,
  Save
} from 'lucide-react';
import { toast } from 'sonner';
import { blogService } from '@/services/blogService';
import { apiClient } from '@/lib/api-client';

export function BlogManagement() {
  const [activeTab, setActiveTab] = useState('blogs');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  // Inline, window-fitted create form (replaces popup dialog)
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPost, setEditingPost] = useState<any>(null);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; postId: string | null; postTitle: string }>({ open: false, postId: null, postTitle: '' });

  // Remove mock data: always start empty and use backend data only
  const defaultMockBlogs: any[] = [];

  const [blogs, setBlogs] = useState<any[]>([]);

  // Remove auto-save on every change to avoid heavy JSON stringify and network writes
  // Saving is now explicit via the "Save" button only.

  useEffect(() => {
    const normalize = (b: any) => ({
      ...b,
      _id: b?._id || b?.id || String(b?._id || b?.id || Date.now()),
      id: b?._id || b?.id || String(b?._id || b?.id || Date.now())
    });
    const loadBlogs = async () => {
      try {
        // Limit initial payload to reduce render cost on load
        const res = await blogService.list({ limit: 20 });
        const list = Array.isArray(res) ? res : (res?.blogs || res?.items || []);
        if (Array.isArray(list) && list.length) {
          setBlogs(dedupeBlogs(list.map(normalize)));
          return;
        }
      } catch (err) {
        console.warn('Failed to load blogs from backend, falling back:', err);
      }
      // No local/mock fallback: show empty list if backend returns nothing
      setBlogs(defaultMockBlogs);
    };

    loadBlogs();
  }, []);

  const [newPost, setNewPost] = useState({
    title: '',
    content: '',
    category: '',
    featured: false,
    seoTitle: '',
    seoDescription: '',
    status: 'published',
    author: 'Super Admin',
    type: 'article',
    cover_image_url: '',
    video_url: ''
  });

  const categories = [
    'Career Tips',
    'Industry News',
    'Company Spotlight',
    'Job Market Trends',
    'Interview Guides',
    'Resume Writing',
    'Salary Insights',
    'Work Culture'
  ];

  // Helpers to derive text from HTML content and build snippets
  const stripHtml = (html: string) => String(html || '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();

  const getExcerpt = (post: any, length = 140) => {
    const source = post?.description || post?.excerpt || post?.content || '';
    const text = stripHtml(source);
    if (!text) return '';
    const snip = text.slice(0, length);
    return snip.length < text.length ? snip + '…' : snip;
  };

  const filteredBlogs = React.useMemo(() => {
    const term = searchTerm.toLowerCase();
    return blogs.filter((blog) => {
      const title = String(blog.title || '').toLowerCase();
      const contentText = stripHtml(blog.content || '').toLowerCase();
      const tagsArr = Array.isArray(blog.tags) ? blog.tags : String(blog.tags || '').split(',');
      const matchesSearch = title.includes(term) ||
        contentText.includes(term) ||
        tagsArr.some((tag: string) => String(tag).toLowerCase().includes(term));
      const matchesStatus = statusFilter === 'all' || blog.status === statusFilter;
      const matchesCategory = categoryFilter === 'all' || blog.category === categoryFilter;
      return matchesSearch && matchesStatus && matchesCategory;
    });
  }, [blogs, searchTerm, statusFilter, categoryFilter]);

  // Dedupe helper to prevent duplicate entries by slug/id
  const dedupeBlogs = (list: any[]) => {
    const map = new Map<string, any>();
    for (const b of list) {
      const key = String(b?.slug || b?.id || b?._id || b?.title || Math.random());
      // Last write wins
      map.set(key, b);
    }
    return Array.from(map.values());
  };

  const handleCreatePost = async () => {
    if (!newPost.title.trim()) {
      toast.error('Title is required');
      return;
    }
    const payload: any = {
      title: newPost.title,
      slug: newPost.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
      content: newPost.content,
      description: newPost.content,
      excerpt: newPost.content,
      author: newPost.author || 'Super Admin',
      category: newPost.category,
      status: newPost.status,
      type: newPost.type || 'article',
      published: newPost.status === 'published',
      published_at: new Date().toISOString(),
      featured: newPost.featured,
      seoTitle: newPost.seoTitle,
      seoDescription: newPost.seoDescription,
      cover_image_url: newPost.cover_image_url,
      video_url: newPost.video_url
    };
    try {
      const created = await blogService.create(payload);
      const normalizedFromBackend = (Array.isArray(created) ? created[0] : created) || {};
      // Ensure critical fields like `status`, `published`, and `published_at` are present
      const normalized = { 
        ...payload,
        ...normalizedFromBackend
      } as any;
      const withId = { ...normalized, id: (normalized as any)?.id || (normalized as any)?._id || String(Date.now()) };
      const updatedList = dedupeBlogs([ withId, ...blogs ]);
      setBlogs(updatedList);
      setNewPost({ title: '', content: '', category: '', featured: false, seoTitle: '', seoDescription: '', status: 'draft', author: 'Super Admin', type: 'article', cover_image_url: '', video_url: '' });
      toast.success('Blog post created');
      setShowCreateForm(false);
    } catch (err: any) {
      try {
        const updatedList = dedupeBlogs([ { id: Date.now().toString(), ...payload }, ...blogs ]);
        const res = await apiClient.saveBlogs(updatedList);
        const saved = Array.isArray((res as any)?.blogs) ? (res as any).blogs : (Array.isArray(res) ? (res as any) : []);
        setBlogs(dedupeBlogs(saved.length ? saved : updatedList));
        setNewPost({ title: '', content: '', category: '', featured: false, seoTitle: '', seoDescription: '', status: 'draft', author: 'Super Admin', type: 'article', cover_image_url: '', video_url: '' });
        setShowCreateForm(false);
        toast.success('Blog post created (fallback via bulk save)');
      } catch (err2: any) {
        console.error('Create blog failed, saving locally:', err2);
        const fallback = { id: Date.now().toString(), ...payload };
        setBlogs(dedupeBlogs([fallback, ...blogs]));
        setShowCreateForm(false);
        toast.warning(err2?.message ? `Backend unavailable: ${String(err2.message)}` : 'Backend unavailable. Created locally as fallback.');
      }
    }
  };

  const handleEditPost = (post: any) => {
    setEditingPost({ ...post });
  };

  const handleUpdatePost = async () => {
    if (!editingPost) return;
    const id = editingPost.id || editingPost._id;
    const updatePayload: any = {
      title: editingPost.title,
      content: editingPost.content,
      description: editingPost.content,
      excerpt: editingPost.excerpt || editingPost.content,
      category: editingPost.category,
      status: editingPost.status,
      type: editingPost.type || 'article',
      featured: !!editingPost.featured,
      author: editingPost.author,
      cover_image_url: editingPost.cover_image_url,
      video_url: editingPost.video_url
    };
    try {
      await blogService.update(id, updatePayload);
      setBlogs(dedupeBlogs(blogs.map(blog => 
        (blog.id === id || blog._id === id)
          ? { ...blog, ...updatePayload }
          : blog
      )));
      setEditingPost(null);
      toast.success('Blog post updated successfully!');
    } catch (err) {
      console.error('Update blog failed, applying local change:', err);
      const updatedList = dedupeBlogs(blogs.map(blog => 
        (blog.id === id || blog._id === id)
          ? { ...blog, ...updatePayload }
          : blog
      ));
      try {
        const res = await apiClient.saveBlogs(updatedList);
        const saved = Array.isArray((res as any)?.blogs) ? (res as any).blogs : (Array.isArray(res) ? (res as any) : []);
        setBlogs(dedupeBlogs(saved.length ? saved : updatedList));
        setEditingPost(null);
        toast.success('Blog post updated (fallback via bulk save)');
      } catch (err2: any) {
        setBlogs(updatedList);
        setEditingPost(null);
        toast.warning(err2?.message ? `Backend unavailable: ${String(err2.message)}` : 'Backend unavailable. Updated locally as fallback.');
      }
    }
  };

  const handleDeletePost = async (id: string) => {
    try {
      await blogService.remove(id);
      const updated = blogs.filter(blog => (blog.id !== id && blog._id !== id));
      setBlogs(dedupeBlogs(updated));
      toast.success('Blog post deleted successfully!');
    } catch (err) {
      console.error('Delete blog failed, removing locally:', err);
      const updated = blogs.filter(blog => (blog.id !== id && blog._id !== id));
      const updatedList = dedupeBlogs(updated);
      try {
        const res = await apiClient.saveBlogs(updatedList);
        const saved = Array.isArray((res as any)?.blogs) ? (res as any).blogs : (Array.isArray(res) ? (res as any) : []);
        setBlogs(dedupeBlogs(saved.length ? saved : updatedList));
        toast.success('Blog post deleted (fallback via bulk save)');
      } catch (err2: any) {
        setBlogs(updatedList);
        toast.warning(err2?.message ? `Backend unavailable: ${String(err2.message)}` : 'Backend unavailable. Deleted locally as fallback.');
      }
    }
  };

  const togglePostStatus = async (id: string) => {
    const current = blogs.find(b => b.id === id || b._id === id);
    const newStatus = current?.status === 'published' ? 'draft' : 'published';
    try {
      const published_at = newStatus === 'published' ? new Date().toISOString() : null;
      await blogService.update(id, { status: newStatus, published: newStatus === 'published', published_at });
      setBlogs(blogs.map(blog => 
        (blog.id === id || blog._id === id) 
          ? { ...blog, status: newStatus, published_at }
          : blog
      ));
      toast.success('Post status updated!');
    } catch (err) {
      console.error('Status toggle failed, applying locally:', err);
      const updatedList = blogs.map(blog => 
        (blog.id === id || blog._id === id) 
          ? { ...blog, status: newStatus, published_at: newStatus === 'published' ? new Date().toISOString() : null }
          : blog
      );
      try {
        const res = await apiClient.saveBlogs(dedupeBlogs(updatedList));
        const saved = Array.isArray((res as any)?.blogs) ? (res as any).blogs : (Array.isArray(res) ? (res as any) : []);
        setBlogs(dedupeBlogs(saved.length ? saved : updatedList));
        toast.success('Post status updated (fallback via bulk save)');
      } catch (err2: any) {
        setBlogs(dedupeBlogs(updatedList));
        toast.warning(err2?.message ? `Backend unavailable: ${String(err2.message)}` : 'Backend unavailable. Status updated locally.');
      }
    }
  };

  const renderPostForm = (
    post: any,
    setPost: (next: any) => void,
    submitAction: () => void,
    submitLabel: string = 'Create Post'
  ) => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="title">Title</Label>
          <Input id="title" value={post.title} onChange={(e) => setPost({ ...post, title: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="category">Category</Label>
          <Input id="category" value={post.category} onChange={(e) => setPost({ ...post, category: e.target.value })} />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="author">Author</Label>
          <Input id="author" value={post.author || ''} onChange={(e) => setPost({ ...post, author: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="coverImage">Cover Image</Label>
          <div className="flex items-center gap-2">
            <Input id="coverImage" type="file" accept="image/*" onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              try {
                const url = await blogService.uploadCover(file);
                setPost({ ...post, cover_image_url: url });
                toast.success('Cover image uploaded');
              } catch (err: any) {
                toast.error(err?.message || 'Upload failed');
              }
            }} />
          </div>
        </div>
      </div>
      {post.cover_image_url && (
        <div className="mt-2">
          <img src={post.cover_image_url} alt="Cover" className="max-h-40 rounded" />
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="type">Type</Label>
          <Select value={post.type || 'article'} onValueChange={(value) => setPost({ ...post, type: value })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="article">Article</SelectItem>
              <SelectItem value="announcement">Announcement</SelectItem>
              <SelectItem value="youtube">YouTube Video</SelectItem>
              <SelectItem value="video">Video (MP4/Embed)</SelectItem>
              <SelectItem value="image">Image</SelectItem>
              <SelectItem value="banner">Banner</SelectItem>
              <SelectItem value="svg">Animated SVG</SelectItem>
              <SelectItem value="poster">Poster</SelectItem>
              <SelectItem value="link">General Link</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {(post.type === 'video' || post.type === 'youtube') && (
          <div className="space-y-2">
            <Label htmlFor="videoUrl">Video URL</Label>
            <Input id="videoUrl" value={post.video_url || ''} onChange={(e) => setPost({ ...post, video_url: e.target.value })} />
          </div>
        )}
        {post.type === 'svg' && (
          <div className="space-y-2">
            <Label htmlFor="svgUpload">Animated SVG</Label>
            <Input id="svgUpload" type="file" accept=".svg,image/svg+xml" onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              try {
                const url = await blogService.uploadCover(file);
                setPost({ ...post, cover_image_url: url });
                toast.success('SVG uploaded');
              } catch (err: any) {
                toast.error(err?.message || 'SVG upload failed');
              }
            }} />
            {post.cover_image_url && (
              <div className="mt-2">
                <img src={post.cover_image_url} alt="SVG Preview" className="max-h-40 rounded" />
              </div>
            )}
          </div>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="content">Description</Label>
        <RichTextEditor 
          value={post.content} 
          onChange={(value) => setPost({ ...post, content: value })} 
          placeholder="Write your blog description here..."
          showImageUpload={true}
          showLinkInsert={true}
        />
      </div>
      {/* External Link and Tags removed */}
      <div className="flex justify-end">
        <Button onClick={submitAction}>{submitLabel}</Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold">Blog Management</h2>
          <p className="text-gray-600">Manage blog posts and content</p>
        </div>
        <Button onClick={async () => { try { await apiClient.saveBlogs(blogs); toast.success('Content saved'); } catch { toast.error('Failed to save content'); } }} className="flex items-center gap-2">
          <Save className="h-4 w-4" /> Save
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <div className="flex justify-center mb-8">
          <Menubar className="h-12 bg-white/80 backdrop-blur-md border border-gray-200/50 rounded-full p-1 shadow-lg shadow-gray-200/20 relative w-full">
            {[
              { id: 'blogs', label: 'Blog Posts', icon: FileText }
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <MenubarMenu key={tab.id} className="flex-1">
                  <MenubarTrigger
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`
                      relative z-10 flex cursor-pointer items-center justify-center gap-2 rounded-full px-6 py-2 text-sm font-medium transition-all duration-300 w-full
                      ${isActive ? 'text-white font-semibold' : 'text-gray-500 hover:text-gray-900'}
                      bg-transparent border-none shadow-none focus:bg-transparent data-[state=open]:bg-transparent
                    `}
                  >
                    <Icon className={`h-4 w-4 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                    <span className="hidden sm:inline">{tab.label}</span>
                    {isActive && (
                      <motion.div
                        layoutId="activeBlogTabIndicator"
                        className="absolute inset-0 bg-[#e37300] rounded-full shadow-sm"
                        style={{ zIndex: -1 }}
                        transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                      />
                    )}
                  </MenubarTrigger>
                </MenubarMenu>
              );
            })}
          </Menubar>
        </div>

        {/* Blog Posts */}
        <TabsContent value="blogs" className="space-y-6">
          {/* Search and Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Search blog posts..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => setShowCreateForm(true)} className="flex items-center gap-2">
                    <Plus className="h-4 w-4" /> New Post
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Inline Create Form (window-fitted) */}
          {showCreateForm && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Create New Post</CardTitle>
                <Button variant="outline" onClick={() => setShowCreateForm(false)}>Cancel</Button>
              </CardHeader>
              <CardContent className="pt-2">
                {renderPostForm(newPost, setNewPost, handleCreatePost, 'Create Post')}
              </CardContent>
            </Card>
          )}

          {/* Inline Edit Form (window-fitted) */}
          {editingPost && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Edit Post</CardTitle>
                <Button variant="outline" onClick={() => setEditingPost(null)}>Cancel</Button>
              </CardHeader>
              <CardContent className="pt-2">
                {renderPostForm(editingPost, setEditingPost, handleUpdatePost, 'Update Post')}
              </CardContent>
            </Card>
          )}

          {/* Blog List */}
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4">
                {filteredBlogs.map((blog: any) => (
                  <div key={blog.id} className=" p-4">
                    <div className="flex justify-between items-start">
                      <div className="space-y-2">
                        <h3 className="text-lg font-semibold">{blog.title}</h3>
                        <p className="text-sm text-gray-600">{getExcerpt(blog, 160)}</p>
                        <div className="text-xs text-gray-500">Category: {blog.category} • Status: {blog.status} • Type: {blog.type || 'article'}</div>
                        <div className="text-xs text-gray-500">Author: {blog.author || 'Unknown'} • {blog.published_at ? new Date(blog.published_at).toLocaleDateString() : ''}</div>
                        {blog.cover_image_url && (
                          <img src={blog.cover_image_url} alt="cover" className="mt-2 max-h-24 rounded" />
                        )}
                        {blog.video_url && (
                          <div className="mt-2">
                            <a className="text-xs text-blue-600 underline" href={blog.video_url} target="_blank" rel="noreferrer">Video link</a>
                          </div>
                        )}
                        {/* External link removed from display */}
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleEditPost(blog)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => togglePostStatus(blog._id || blog.id)} className={blog.status === 'published' ? 'text-yellow-600' : 'text-green-600'}>
                          {blog.status === 'published' ? 'Unpublish' : 'Publish'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDeleteDialog({ open: true, postId: String(blog._id || blog.id), postTitle: String(blog.title || '') })}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* News Articles removed — managed under separate News & Video section */}
      </Tabs>

      {/* Create/Edit use inline full-width forms above */}

      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog((prev) => ({ ...prev, open }))}
        onConfirm={async () => {
          const id = String(deleteDialog.postId || '');
          setDeleteDialog((prev) => ({ ...prev, open: false }));
          if (id) await handleDeletePost(id);
        }}
        title="Delete Blog Post"
        description="Are you sure you want to permanently delete this blog post? This action cannot be undone."
        confirmText="Delete Post"
        cancelText="Cancel"
        type="danger"
        itemName={deleteDialog.postTitle}
        variant="minimal"
      />
    </div>
  );
}
